import {
  ActionIcon,
  Avatar,
  Box,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconMail, IconMessage } from '@tabler/icons-react';
import { SearchInput } from '../../../components/SearchInput';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react-hooks';
import { useMemo, useState } from 'react';
import { useResolvedCounterpartyDisplay } from '../hooks/useResolvedCounterpartyDisplay';
import {
  getConversationCounterpartyDisplay,
  getThreadSidebarPreview,
  hasThreadBeenReadByCurrentUser,
} from '../services/messagingFhir';
import { isMessagingParticipantProfile, resolveMessagingCurrentUserRef } from '../services/messagingIdentity';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import classes from './InboxSidebar.module.css';

interface InboxSidebarProps {
  selectedThread: string | null;
  onSelectThread: (conversationId: string, parentMessageId?: string | null) => void;
  onComposeNew: () => void;
  sections?: SectionType[];
  /** All threads to display (already merged with any extra threads). */
  threads: Communication[];
  /** When true, sidebar fills the container (mobile inbox-only view). */
  fullWidth?: boolean;
  /** IANA zone for rendering `sent` instants (Practitioner preference, else browser). */
  displayTimeZone: string;
  localhostOnly?: boolean;
}

/** Single row: All | Clients | Providers | Spaces */
type SectionType = 'all' | 'clients' | 'providers' | 'spaces';

export function InboxSidebar({
  selectedThread,
  onSelectThread,
  onComposeNew,
  sections,
  threads,
  fullWidth = false,
  displayTimeZone,
  localhostOnly,
}: InboxSidebarProps): JSX.Element {
  const profile = useMedplumProfile();
  const currentUserRef =
    profile && !isMessagingParticipantProfile(profile)
      ? undefined
      : resolveMessagingCurrentUserRef(profile, false).reference;
  const [search, setSearch] = useState('');
  const allowedSections: SectionType[] = sections && sections.length > 0 ? sections : ['all', 'clients', 'providers', 'spaces'];
  const [section, setSection] = useState<SectionType>(allowedSections[0] ?? 'all');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const filteredThreads = useMemo(() => {
    let result = threads;

    if (section === 'clients') {
      result = result.filter((thread) => isClientsSectionThread(thread, currentUserRef));
    } else if (section === 'providers') {
      result = result.filter((thread) => isProvidersSectionThread(thread, currentUserRef));
    } else if (section === 'spaces') {
      result = result.filter((thread) => isSpaceThread(thread));
    }

    if (unreadOnly) {
      result = result.filter((thread) => !hasThreadBeenReadByCurrentUser(thread, currentUserRef));
    }

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (thread) =>
          getConversationCounterpartyDisplay(thread, currentUserRef).toLowerCase().includes(searchLower) ||
          thread.subject?.display?.toLowerCase().includes(searchLower) ||
          (thread.topic as { text?: string } | undefined)?.text?.toLowerCase().includes(searchLower) ||
          getThreadSidebarPreview(thread).toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [threads, section, unreadOnly, search, currentUserRef]);

  return (
    <Box className={`${classes.sidebar} ${fullWidth ? classes.sidebarFullWidth : ''}`}>
      {/* Header: title + Unread toggle + compose */}
      <Group justify="space-between" p="md" className={classes.header}>
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} size="lg">
            Messages
          </Text>
          {localhostOnly ? <LocalhostOnlyBadge /> : null}
        </Group>
        <Group gap="xs">
          <Tooltip label={unreadOnly ? 'Show all conversations' : 'Show unread only'}>
            <ActionIcon
              variant={unreadOnly ? 'filled' : 'subtle'}
              color={unreadOnly ? 'unifyGold' : 'gray'}
              onClick={() => setUnreadOnly(!unreadOnly)}
              aria-label={unreadOnly ? 'Show all' : 'Unread only'}
            >
              <IconMail size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Compose new message">
            <ActionIcon variant="filled" color="unifyGold" onClick={onComposeNew} aria-label="Compose new message">
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Search */}
      <Box px="md" pb="sm">
        <SearchInput
          placeholder="Search messages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="sm"
        />
      </Box>

      {allowedSections.length > 1 && (
        <Box px="md" pb="sm">
          <SegmentedControl
            size="xs"
            fullWidth
            value={section}
            onChange={(val) => {
              const next = (val as SectionType) ?? allowedSections[0] ?? 'all';
              if (allowedSections.includes(next)) {
                setSection(next);
              } else {
                setSection(allowedSections[0] ?? 'all');
              }
            }}
            data={allowedSections.map((s) => ({
              label: s === 'all' ? 'All' : s === 'clients' ? 'Clients' : s === 'providers' ? 'Providers' : 'Spaces',
              value: s,
            }))}
          />
        </Box>
      )}

      {/* Conversation List */}
      <ScrollArea style={{ flex: 1 }}>
        {filteredThreads.length === 0 ? (
          <Box className={classes.emptyState}>
            <IconMessage size={40} stroke={1.5} />
            <Text size="sm">No conversations found</Text>
          </Box>
        ) : (
          <Stack gap={0}>
            {filteredThreads.map((thread) => (
              <ConversationItem
                key={thread.id}
                thread={thread}
                currentUserRef={currentUserRef}
                selected={selectedThread === thread.id}
                fullWidth={fullWidth}
                displayTimeZone={displayTimeZone}
                onClick={() => onSelectThread(thread.id!, null)}
              />
            ))}
          </Stack>
        )}
      </ScrollArea>
    </Box>
  );
}

interface ConversationItemProps {
  thread: Communication;
  currentUserRef: string | undefined;
  selected: boolean;
  fullWidth?: boolean;
  displayTimeZone: string;
  onClick: () => void;
}

function ConversationItem({ thread, currentUserRef, selected, fullWidth, displayTimeZone, onClick }: ConversationItemProps): JSX.Element {
  const isUnread = !hasThreadBeenReadByCurrentUser(thread, currentUserRef);
  const code = thread.category?.[0]?.coding?.[0]?.code;
  const isSpace = isSpaceThread(thread);
  const isProvider = code === 'provider-conversation' || code === 'provider-thread' || isSpace;
  const lastMessage = getThreadSidebarPreview(thread);
  const timestamp = formatRelativeTime(thread.sent, displayTimeZone);
  const displayName = useResolvedCounterpartyDisplay(thread, currentUserRef);
  const initials = getInitials(displayName);

  return (
    <Box
      className={`${classes.conversationItem} ${selected ? classes.selected : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <Group gap="sm" wrap="nowrap">
        <Box className={classes.unreadIndicator} data-unread={isUnread} />
        <Avatar size="md" radius="xl" color={isProvider || isSpace ? 'unifySage' : 'unifyGold'}>
          {initials}
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" fw={isUnread ? 600 : 400} truncate style={fullWidth ? { minWidth: 0 } : { maxWidth: 140 }}>
              {displayName}
            </Text>
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              {timestamp}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" truncate>
            {lastMessage}
          </Text>
        </Box>
      </Group>
    </Box>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function participantReferences(comm: Communication): string[] {
  const refs: string[] = [];
  if (comm.sender?.reference) refs.push(comm.sender.reference);
  for (const r of comm.recipient ?? []) {
    if (r.reference) refs.push(r.reference);
  }
  return refs;
}

/** Provider dashboard: threads with a patient. Hidden for patient viewers. */
function isClientsSectionThread(comm: Communication, me: string | undefined): boolean {
  if (isSpaceThread(comm)) return false;
  if (!me?.startsWith('Practitioner/')) return false;
  const code = comm.category?.[0]?.coding?.[0]?.code;
  if (code === 'client-conversation' || code === 'client-thread') {
    return participantReferences(comm).some((r) => r.startsWith('Patient/'));
  }
  return (code === undefined || code === 'client-conversation' || code === 'client-thread') && (comm.subject?.reference?.startsWith('Patient/') ?? false);
}

/**
 * Patient: chats that include a practitioner (client-thread with your providers).
 * Practitioner: colleague chats (provider-thread), not patient conversations.
 */
function isProvidersSectionThread(comm: Communication, me: string | undefined): boolean {
  if (isSpaceThread(comm)) return false;
  const code = comm.category?.[0]?.coding?.[0]?.code;
  const refs = participantReferences(comm);

  if (me?.startsWith('Patient/')) {
    if (code === 'client-conversation' || code === 'client-thread') {
      return refs.some((r) => r.startsWith('Practitioner/'));
    }
    return false;
  }

  if (me?.startsWith('Practitioner/')) {
    if (code === 'provider-conversation' || code === 'provider-thread') return true;
    return comm.subject?.reference?.startsWith('Practitioner/') ?? false;
  }

  return code === 'provider-conversation' || code === 'provider-thread' || (comm.subject?.reference?.startsWith('Practitioner/') ?? false);
}

function isSpaceThread(comm: Communication): boolean {
  const code = comm.category?.[0]?.coding?.[0]?.code;
  if (code) return code === 'space' || code === 'group-thread';
  return (
    comm.subject?.reference?.startsWith('Group/') ||
    comm.subject?.reference?.startsWith('CareTeam/') ||
    false
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatRelativeTime(dateStr: string | undefined, timeZone: string): string {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
}
